// E-mail autocomplete

import trans from 'js/utilities/trans';

const COMMON_DOMAINS = [
    'gmail.com',
    'hotmail.com',
    'hotmail.nl',
    'outlook.com',
    'outlook.nl',
    'live.nl',
    'live.com',
    'icloud.com',
    'me.com',
    'ziggo.nl',
    'kpnmail.nl',
    'planet.nl',
    'home.nl',
    'casema.nl',
    'telfort.nl',
    'xs4all.nl',
    'hetnet.nl',
    'chello.nl',
    'upcmail.nl',
    'zeelandnet.nl',
    'online.nl',
    'caiway.nl',
    'solcon.nl',
    'quicknet.nl',
    'tele2.nl',
    'yahoo.com',
    'msn.com',
    'googlemail.com',
    'protonmail.com',
    'proton.me',
    'gmx.com',
];

const MAX_SUGGESTIONS = 5;
const MIN_TYPO_LENGTH = 3;

let instanceCount = 0;

const distance = (a, b) => {
    const rows = [];

    for (let i = 0; i <= a.length; i++) {
        rows.push([i].concat(new Array(b.length).fill(0)));
    }

    for (let j = 0; j <= b.length; j++) {
        rows[0][j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;

            rows[i][j] = Math.min(
                rows[i - 1][j] + 1,
                rows[i][j - 1] + 1,
                rows[i - 1][j - 1] + cost,
            );

            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
            }
        }
    }

    return rows[a.length][b.length];
};

const findTypoDomain = (domain) => {
    let match = null;
    let smallest = Infinity;

    COMMON_DOMAINS.forEach((candidate) => {
        const maximum = candidate.length <= 6 ? 1 : 2;
        const current = distance(domain, candidate);

        if (current > 0 && current <= maximum && current < smallest) {
            match = candidate;
            smallest = current;
        }
    });

    return match;
};

const splitEmail = (value) => {
    const position = value.lastIndexOf('@');

    if (position < 1 || value.slice(0, position).indexOf(' ') !== -1) {
        return null;
    }

    const domain = value.slice(position + 1);

    if (domain.indexOf(' ') !== -1 || domain.indexOf('@') !== -1) {
        return null;
    }

    return {
        localPart: value.slice(0, position),
        domain: domain.toLowerCase(),
    };
};

const getSuggestions = (value) => {
    const parts = splitEmail(value);

    if (!parts || COMMON_DOMAINS.indexOf(parts.domain) !== -1) {
        return [];
    }

    const matches = COMMON_DOMAINS
        .filter((candidate) => candidate.indexOf(parts.domain) === 0)
        .slice(0, MAX_SUGGESTIONS);

    if (matches.length) {
        return matches.map((domain) => ({
            email: `${parts.localPart}@${domain}`,
            isTypo: false,
        }));
    }

    if (parts.domain.length < MIN_TYPO_LENGTH) {
        return [];
    }

    const typoDomain = findTypoDomain(parts.domain);

    return typoDomain ? [{email: `${parts.localPart}@${typoDomain}`, isTypo: true}] : [];
};

const createAutocomplete = (input) => {
    instanceCount += 1;

    const id = `email-autocomplete-${instanceCount}`;
    const list = document.createElement('ul');
    let suggestions = [];
    let activeIndex = -1;

    list.id = id;
    list.className = 'email-autocomplete__list list-reset';
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', trans('app.email_autocomplete.label', {}, 'E-mailsuggesties'));

    input.parentElement.classList.add('email-autocomplete');
    input.insertAdjacentElement('afterend', list);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);
    input.setAttribute('aria-autocomplete', 'list');

    const isOpen = () => !list.hidden;

    const setActive = (index) => {
        activeIndex = index;

        Array.from(list.children).forEach((option, optionIndex) => {
            const isActive = optionIndex === index;

            option.classList.toggle('email-autocomplete__option--active', isActive);
            option.setAttribute('aria-selected', isActive ? 'true' : 'false');

            if (isActive) {
                input.setAttribute('aria-activedescendant', option.id);
                option.scrollIntoView({block: 'nearest'});
            }
        });

        if (index < 0) {
            input.removeAttribute('aria-activedescendant');
        }
    };

    const close = () => {
        list.hidden = true;
        list.textContent = '';
        suggestions = [];
        input.setAttribute('aria-expanded', 'false');
        setActive(-1);
    };

    const select = (index) => {
        if (!suggestions[index]) {
            return;
        }

        input.value = suggestions[index].email;
        close();

        input.dispatchEvent(new Event('input', {bubbles: true}));
        input.dispatchEvent(new Event('change', {bubbles: true}));
    };

    const positionList = () => {
        list.style.top = `${input.offsetTop + input.offsetHeight}px`;
        list.style.left = `${input.offsetLeft}px`;
        list.style.width = `${input.offsetWidth}px`;
    };

    const render = () => {
        list.textContent = '';

        suggestions.forEach((suggestion, index) => {
            const option = document.createElement('li');

            option.id = `${id}-option-${index}`;
            option.className = 'email-autocomplete__option';
            option.setAttribute('role', 'option');
            option.setAttribute('aria-selected', 'false');
            option.textContent = suggestion.email;

            if (suggestion.isTypo) {
                const note = document.createElement('span');

                note.className = 'email-autocomplete__note';
                note.textContent = trans('app.email_autocomplete.did_you_mean', {}, 'Bedoelde je dit?');
                option.appendChild(note);
            }

            list.appendChild(option);
        });
    };

    const update = () => {
        suggestions = getSuggestions(input.value);

        if (!suggestions.length) {
            close();
            return;
        }

        render();
        positionList();
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        setActive(-1);
    };

    input.addEventListener('input', update);

    input.addEventListener('focus', update);

    input.addEventListener('blur', close);

    input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen()) {
            event.preventDefault();
            close();
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!isOpen()) {
                update();

                if (!isOpen()) {
                    return;
                }
            }

            event.preventDefault();

            const last = suggestions.length - 1;

            if (event.key === 'ArrowDown') {
                setActive(activeIndex < last ? activeIndex + 1 : 0);
            } else {
                setActive(activeIndex < 1 ? last : activeIndex - 1);
            }

            return;
        }

        if ((event.key === 'Enter' || event.key === 'Tab') && isOpen() && activeIndex > -1) {
            if (event.key === 'Enter') {
                event.preventDefault();
            }

            select(activeIndex);
        }
    });

    list.addEventListener('mousedown', (event) => event.preventDefault());

    list.addEventListener('click', (event) => {
        const option = event.target.closest('.email-autocomplete__option');

        if (option) {
            select(Array.from(list.children).indexOf(option));
            input.focus();
        }
    });

    if (input.form) {
        input.form.addEventListener('reset', close);
    }
};

const SELECTOR = 'input[type="email"], input[autocomplete="email"]';

const initialized = new WeakSet();

const initialize = (input) => {
    if (!input.parentElement || initialized.has(input)) {
        return;
    }

    initialized.add(input);
    createAutocomplete(input);
};

document.querySelectorAll(SELECTOR).forEach(initialize);

document.addEventListener('focusin', (event) => {
    if (event.target.matches && event.target.matches(SELECTOR)) {
        initialize(event.target);
    }
});
